<script setup lang="ts">
  import { debounce } from 'es-toolkit';
  import { uniqueId } from 'es-toolkit/compat';
  import { computed, inject, ref, useSlots, watch } from 'vue';

  import type { StashProvideState } from '../../../types/misc';
  import useGoogleMaps, { type Address, type AddressOption } from '../../composables/useGoogleMaps/useGoogleMaps';
  import { DEBOUNCE } from '../../constants';
  import Select from '../Select/Select.vue';

  export interface AddressSelectProps {
    /**
     * Google Maps API Key. This takes precedence over the key defined in stashOptions
     */
    apiKey?: string;
    /**
     * Wether the Select is disabled or not
     */
    disabled?: boolean;
    /**
     * Select placeholder
     */
    placeholder?: string;
    /**
     * Debounce time in milliseconds to perform API requests on search
     */
    debounceTime?: number;
    /**
     * Model value address
     */
    modelValue?: Address;
    /**
     * Hint text to be passed down to Select component
     */
    hintText?: string;
    /**
     * Error text to be passed down to Select component
     */
    errorText?: string;
    /**
     * Label text to be passed down to Select component
     */
    label?: string;
    /**
     * Disallows values from being de-selected
     */
    preventEmpty?: boolean;
  }

  const props = withDefaults(defineProps<AddressSelectProps>(), {
    disabled: false,
    placeholder: 'Type an address',
    debounceTime: DEBOUNCE.MEDIUM,
    modelValue: () => ({
      street_address: undefined,
      extended_address: undefined,
      city: undefined,
      state: undefined,
      postal_code: undefined,
      country: undefined,
    }),
    hintText: undefined,
    errorText: undefined,
    label: undefined,
    preventEmpty: false,
    apiKey: undefined,
  });

  const emit = defineEmits<{
    /**
     * Emitted when the model value changes
     */
    (e: 'update:modelValue', value: Address): void;
    /**
     * Emitted when an error occurs while reaching out to Google's API
     */
    (e: 'error', value: unknown): void;
  }>();

  const slots = useSlots();
  const stashOptions = inject<StashProvideState>('stashOptions');

  const googleMapsApiKey = computed(() => props.apiKey || stashOptions?.googleMapsApiKey);

  if (!googleMapsApiKey.value) {
    throw new Error('Google Maps API key is required');
  }

  const { getPlaceDetails, getPlacePredictions } = useGoogleMaps(googleMapsApiKey.value);

  const ID_PREFIX = 'address-select-';

  const isLoading = ref(false);
  const options = ref<Array<AddressOption>>();
  const internalValue = ref<AddressOption>();

  const debouncedSearch = debounce(search, props.debounceTime);

  const formattedAddress = computed(() => {
    const formattedAddress = [
      [props.modelValue?.street_address, props.modelValue?.extended_address].filter(Boolean).join(' '),
      props.modelValue?.city,
      [props.modelValue?.state, props.modelValue?.postal_code].filter(Boolean).join(' '),
      props.modelValue?.country,
    ]
      .filter(Boolean)
      .join(', ');

    return formattedAddress;
  });

  // When we don't have options just yet, we need to create them on our own
  watch(
    () => props.modelValue,
    () => {
      if (formattedAddress.value && !options.value?.length) {
        const option = {
          id: props.modelValue.place_id || uniqueId(ID_PREFIX),
          name: formattedAddress.value,
          address: props.modelValue,
        };

        options.value = [option];
        internalValue.value = option;
      } else if (!formattedAddress.value) {
        internalValue.value = undefined;
        options.value = [];
      }
    },
    { immediate: true },
  );

  async function search(query: string) {
    isLoading.value = true;

    try {
      options.value = await getPlacePredictions(query);
    } catch (error: any) {
      options.value = [];
      if (error?.isGoogleMapsError) {
        emit('error', error);
      } else {
        throw error;
      }
    } finally {
      isLoading.value = false;
    }
  }

  async function select(option: AddressOption) {
    internalValue.value = option;

    // When an option is de-selected, the option will be undefined
    if (!option) {
      emit('update:modelValue', {
        street_address: undefined,
        extended_address: undefined,
        city: undefined,
        state: undefined,
        postal_code: undefined,
        country: undefined,
      });
      return;
    }

    // When an option does not have a google place id, we don't want to fetch it's details
    if (option.id.startsWith(ID_PREFIX) && option.address) {
      emit('update:modelValue', option.address);
      return;
    }

    try {
      const address = await getPlaceDetails(option.id);
      emit('update:modelValue', address);
    } catch (error: any) {
      if (error?.isGoogleMapsError) {
        emit('error', error);
      } else {
        throw error;
      }
    }
  }
</script>

<template>
  <Select
    single
    data-test="select|address"
    :disabled="props.disabled"
    :loading="isLoading"
    :options="options"
    :placeholder="props.placeholder"
    :label="props.label"
    :hint-text="props.hintText"
    :error="props.errorText"
    :model-value="internalValue"
    :prevent-empty="props.preventEmpty"
    disable-filtering
    @search="debouncedSearch"
    @update:model-value="select"
  >
    <template v-if="slots.hint" #hint>
      <slot name="hint"></slot>
    </template>
  </Select>
</template>
